import React, { useState, useEffect } from 'react';
import {
    Table, Button, Space, Input,
    message, Popconfirm, Typography, Tag, Badge, Tooltip,
    Row, Col, Select
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined,
    PrinterOutlined, SearchOutlined, FilterOutlined,
    FileExcelOutlined, ApiOutlined, LinkOutlined,
    KeyOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchPrinterCategories,
    createPrinterCategory,
    updatePrinterCategory,
    deletePrinterCategory,
    updatePrinterCategoriesStatus,
    searchCategories,
    filterByStatus,
    filterByCode,
    changePagination,
    clearFilters,
    fetchAvailableCodes
} from '../actions';
import PrinterModal from './PrinterModal';

const { Title, Text } = Typography;
const { Option } = Select;

const PrinterSettings = () => {
    const dispatch = useDispatch();

    // Add defensive programming for selector
    const printerCategoriesState = useSelector(state => {
        console.log('Redux state:', state); // Debug log
        return state.categoryManagement?.printerCategories || {
            categories: [],
            loading: false,
            pagination: { current: 1, pageSize: 10, total: 0 },
            filters: { searchText: '', statusFilter: 'all', codeFilter: 'all' }
        };
    });

    // Ensure categories is always an array
    const { categories = [], loading = false, pagination = {}, filters = {} } = printerCategoriesState;

    // Debug log to see what we're getting
    console.log('Printers data:', categories, 'Type:', typeof categories, 'Is Array:', Array.isArray(categories));

    const [modalVisible, setModalVisible] = useState(false);
    const [editingPrinter, setEditingPrinter] = useState(null);

    // Local state for immediate UI updates (will be synced with Redux on API call)
    const [localSearchText, setLocalSearchText] = useState(filters.searchText || '');
    const [localStatusFilter, setLocalStatusFilter] = useState(filters.statusFilter || 'all');
    const [localCodeFilter, setLocalCodeFilter] = useState(filters.codeFilter || 'all');

    // State for available codes
    const [availableCodes, setAvailableCodes] = useState([]);

    useEffect(() => {
        // Load data with default filters on component mount
        dispatch(fetchPrinterCategories({
            page: 1,
            pageSize: 10,
            searchText: '',
            statusFilter: 'all',
            codeFilter: 'all'
        }));

        // Load available codes for filter dropdown
        loadAvailableCodes();
    }, [dispatch]);

    // Load available codes
    const loadAvailableCodes = async () => {
        const result = await dispatch(fetchAvailableCodes());
        if (result.success) {
            setAvailableCodes(result.data);
        }
    };

    // Sync local state with Redux state when filters change
    useEffect(() => {
        setLocalSearchText(filters.searchText || '');
        setLocalStatusFilter(filters.statusFilter || 'all');
        setLocalCodeFilter(filters.codeFilter || 'all');
    }, [filters]);

    const handleCreatePrinter = () => {
        setEditingPrinter(null);
        setModalVisible(true);
    };

    const handleEditPrinter = (printer) => {
        setEditingPrinter(printer);
        setModalVisible(true);
    };

    const handleDeletePrinter = async (id) => {
        try {
            await dispatch(deletePrinterCategory(id));
            message.success('Printer deleted successfully');
            // Refresh data after deletion
            dispatch(fetchPrinterCategories({
                page: pagination.current || 1,
                pageSize: pagination.pageSize || 10,
                ...filters
            }));
        } catch (error) {
            message.error('Failed to delete printer');
        }
    };

    // Function to generate Google Sheet URL from Sheet ID
    const generateGoogleSheetUrl = (sheetId) => {
        if (!sheetId) return null;
        return `https://docs.google.com/spreadsheets/d/${sheetId}/edit`;
    };

    const handleModalOk = async (printerData, editingPrinter) => {
        try {
            if (editingPrinter) {
                await dispatch(updatePrinterCategory(editingPrinter.id, printerData));
                message.success('Printer updated successfully');
            } else {
                await dispatch(createPrinterCategory(printerData));
                message.success('Printer created successfully');
            }

            setModalVisible(false);

            // Refresh data after create/update
            dispatch(fetchPrinterCategories({
                page: pagination.current || 1,
                pageSize: pagination.pageSize || 10,
                ...filters
            }));

            // Reload available codes
            loadAvailableCodes();
        } catch (error) {
            message.error('An error occurred');
        }
    };

    const handleModalCancel = () => {
        setModalVisible(false);
        setEditingPrinter(null);
    };

    const handleStatusChange = async (id, isActive) => {
        try {
            await dispatch(updatePrinterCategoriesStatus([id], isActive));
            message.success(`Printer ${isActive ? 'activated' : 'deactivated'} successfully`);
        } catch (error) {
            message.error('Failed to update status');
        }
    };

    const getStatusBadge = (isActive) => {
        return isActive ? (
            <Badge status="success" text="Active" />
        ) : (
            <Badge status="default" text="Inactive" />
        );
    };

    // Enhanced backend filtering handlers
    const handleSearchChange = (value) => {
        setLocalSearchText(value);
    };

    const handleSearchSubmit = (value) => {
        dispatch(searchCategories(value));
    };

    const handleStatusFilterChange = (value) => {
        setLocalStatusFilter(value);
        dispatch(filterByStatus(value));
    };

    const handleCodeFilterChange = (value) => {
        setLocalCodeFilter(value);
        dispatch(filterByCode(value));
    };

    const handleClearFilters = () => {
        setLocalSearchText('');
        setLocalStatusFilter('all');
        setLocalCodeFilter('all');
        dispatch(clearFilters());
    };

    // Handle table pagination changes
    const handleTableChange = (paginationInfo, filtersInfo, sorter) => {
        dispatch(changePagination(paginationInfo.current, paginationInfo.pageSize));
    };

    // Check if any filters are active
    const hasActiveFilters = localSearchText || localStatusFilter !== 'all' || localCodeFilter !== 'all';

    // Count active filters
    const getActiveFiltersCount = () => {
        let count = 0;
        if (localSearchText) count++;
        if (localStatusFilter !== 'all') count++;
        if (localCodeFilter !== 'all') count++;
        return count;
    };

    const columns = [
        {
            title: 'No.',
            key: 'index',
            width: 60,
            render: (_, __, index) => {
                const current = pagination.current || 1;
                const pageSize = pagination.pageSize || 10;
                return (current - 1) * pageSize + index + 1;
            }
        },
        {
            title: 'Printer Name',
            dataIndex: 'name',
            key: 'name',
            width: 200,
            render: (text, record) => (
                <Space>
                    <PrinterOutlined style={{ color: '#1890ff' }} />
                    <Text strong>{text}</Text>
                </Space>
            )
        },
        {
            title: 'Printer Code',
            dataIndex: 'code',
            key: 'code',
            width: 150,
            render: code => code ? code : '-'
        },
        {
            title: 'Fulfill Mode',
            dataIndex: 'fulfillMode',
            key: 'fulfillMode',
            width: 160,
            render: (mode) => {
                const modeConfig = {
                    sheet: { color: 'green', icon: <FileExcelOutlined />, text: 'Google Sheet' },
                    api: { color: 'blue', icon: <ApiOutlined />, text: 'Printer API' }
                };
                const config = modeConfig[mode] || modeConfig.sheet;
                return (
                    <Tag color={config.color} icon={config.icon}>
                        {config.text}
                    </Tag>
                );
            }
        },
        {
            title: 'Google Sheet ID',
            dataIndex: 'sheetId',
            key: 'sheetId',
            width: 220,
            ellipsis: true,
            render: (sheetId) => {
                if (!sheetId) return '-';
                const shortId = sheetId.length > 15 ? `${sheetId.substring(0, 15)}...` : sheetId;
                const sheetUrl = generateGoogleSheetUrl(sheetId);
                return (
                    <Tooltip title={`Sheet ID: ${sheetId}`}>
                        <a href={sheetUrl} target="_blank" rel="noopener noreferrer">
                            <LinkOutlined /> {shortId}
                        </a>
                    </Tooltip>
                );
            }
        },
        {
            title: 'API Key',
            dataIndex: 'apiKey',
            key: 'apiKey',
            width: 150,
            render: (apiKey, record) => {
                if (!apiKey || record.fulfillMode !== 'api') return '-';
                const maskedKey = apiKey.length > 8 ? 
                    `${apiKey.substring(0, 4)}***${apiKey.substring(apiKey.length - 4)}` : 
                    '***';
                return (
                    <Tooltip title="API Key (masked for security)">
                        <Text type="secondary" code>
                            <KeyOutlined style={{ marginRight: 4, color: '#1890ff' }} />
                            {maskedKey}
                        </Text>
                    </Tooltip>
                );
            }
        },
        {
            title: 'Products Synced',
            dataIndex: 'productsSynced',
            key: 'productsSynced',
            width: 160,
            render: (_, record) => {
                if (record.fulfillMode !== 'api') return '-';
                
                const hasProducts = record.printerProducts && record.printerProducts.length > 0;
                const latestSync = hasProducts ? record.printerProducts[0].createdAt : null;
                
                if (hasProducts) {
                    return (
                        <Tooltip title={`Last synced: ${latestSync ? new Date(latestSync).toLocaleString() : 'Unknown'}`}>
                            <Badge status="success" text="Synced" />
                        </Tooltip>
                    );
                } else {
                    return <Badge status="default" text="Not Synced" />;
                }
            }
        },
        {
            title: 'Status',
            dataIndex: 'isActive',
            key: 'isActive',
            width: 130,
            render: (isActive, record) => (
                getStatusBadge(isActive)
            )
        },
        {
            title: 'Created Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 150,
            render: text => new Date(text).toLocaleDateString('en-US')
        },
        {
            title: 'Actions',
            key: 'action',
            width: 85,
            fixed: 'right',
            align: 'center',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="Edit">
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => handleEditPrinter(record)}
                            size="small"
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Are you sure you want to delete this printer?"
                        onConfirm={() => handleDeletePrinter(record.id)}
                        okText="Delete"
                        cancelText="Cancel"
                    >
                        <Tooltip title="Delete">
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                size="small"
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div className="printer-settings">
            {/* Enhanced Filter Section */}
            <Row style={{ marginBottom: 16 }} gutter={16} align="middle">
                <Col flex="auto">
                    <Space size="middle">
                        <Input.Search
                            style={{ width: 300 }}
                            placeholder="Search by name, contact, email or code..."
                            allowClear
                            value={localSearchText}
                            onChange={e => handleSearchChange(e.target.value)}
                            onSearch={handleSearchSubmit}
                        />

                        <Select
                            placeholder="Filter by status"
                            value={localStatusFilter}
                            onChange={handleStatusFilterChange}
                            style={{ width: 150 }}
                        >
                            <Option value="all">All Status</Option>
                            <Option value="active">Active</Option>
                            <Option value="inactive">Inactive</Option>
                        </Select>

                        <Select
                            placeholder="Filter by code"
                            value={localCodeFilter}
                            onChange={handleCodeFilterChange}
                            style={{ width: 150 }}
                            showSearch
                            optionFilterProp="children"
                            loading={availableCodes.length === 0}
                        >
                            <Option value="all">All Codes</Option>
                            {availableCodes.map(code => (
                                <Option key={code} value={code}>{code}</Option>
                            ))}
                        </Select>

                        {hasActiveFilters && (
                            <Button onClick={handleClearFilters} icon={<FilterOutlined />}>
                                Clear Filters ({getActiveFiltersCount()})
                            </Button>
                        )}
                    </Space>
                </Col>
                <Col>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleCreatePrinter}
                    >
                        Add Printer
                    </Button>
                </Col>
            </Row>

            {/* Active filters display */}
            {hasActiveFilters && (
                <Row style={{ marginBottom: 16 }}>
                    <Col span={24}>
                        <Space wrap>
                            <Text type="secondary">Active filters:</Text>
                            {localSearchText && (
                                <Tag color="blue" closable onClose={() => {
                                    setLocalSearchText('');
                                    dispatch(searchCategories(''));
                                }}>
                                    Search: {localSearchText}
                                </Tag>
                            )}
                            {localStatusFilter !== 'all' && (
                                <Tag color="green" closable onClose={() => handleStatusFilterChange('all')}>
                                    Status: {localStatusFilter === 'active' ? 'Active' : 'Inactive'}
                                </Tag>
                            )}
                            {localCodeFilter !== 'all' && (
                                <Tag color="orange" closable onClose={() => handleCodeFilterChange('all')}>
                                    Code: {localCodeFilter}
                                </Tag>
                            )}
                        </Space>
                    </Col>
                </Row>
            )}

            <Table
                dataSource={Array.isArray(categories) ? categories.map(printer => ({ ...printer, key: printer.id })) : []}
                columns={columns}
                loading={loading}
                pagination={{
                    current: pagination.current || 1,
                    pageSize: pagination.pageSize || 10,
                    total: pagination.total || 0,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} printers`,
                    pageSizeOptions: ['10', '20', '50', '100']
                }}
                onChange={handleTableChange}
                className="printer-table"
                scroll={{ x: 1400 }}
            />

            <PrinterModal
                visible={modalVisible}
                editingPrinter={editingPrinter}
                loading={loading}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
            />
        </div>
    );
};

export default PrinterSettings;